import {useIntl, defineMessages, FormattedMessage} from 'react-intl';
import {Action, view, Icon} from '@sail/ui';
import {chevronRight, external} from '@sail/icons/react/Icon';
import {getStore} from 'src/v2/state/store';
import {COLORS} from 'src/v2/util/color';
import type {NetworkType} from 'src/v2/state/types';
import {Text, BrandedButton} from 'src/v2/components/common';
import type {StartWalletConnection} from 'src/v2/components/walletConnect/useIsWalletConnecting';
import {isNetworkDisabledForProvider} from 'src/v2/util/isNetworkDisabledForProvider';
import {getDisabledNetworksForProvider} from 'src/v2/util/isWalletProviderDisabled';
import {buildNetworksFromSupportedCurrencies} from './buildNetworksFromSupportedCurrencies';
import {GaslessPromotion} from './GaslessPromotion';
import {NetworkIconsStack} from './NetworkIconsStack';

const messages = defineMessages({
  connectWallet: {
    id: 'crypto.payins.v2.network_selector.connect_wallet_button',
    description: 'Label for the connect wallet button',
    defaultMessage: 'Connect wallet',
  },
});

const networkRowStyles = {
  stack: 'x',
  backgroundColor: COLORS.offsetOnLight,
  borderRadius: 'small',
  paddingX: 'space.150',
  paddingY: 'medium',
  alignY: 'center',
  distribute: 'space-between',
  width: 'fill',
} as const;

export const NetworkSelector = ({
  startWalletConnection,
}: {
  startWalletConnection: StartWalletConnection;
}) => {
  const store = getStore();
  const intl = useIntl();

  const isWalletConnecting = store.useSelector(
    (state) => state.overlay === 'CONNECT_WALLET',
  );

  const newWallet = store.useSelector((state) => state.newWallet);
  const supportedCurrencies = store.useSelector(
    (state) => state.supportedCurrencies,
  );
  const disabledWalletProviders = store.useSelector(
    (state) => state.disabledWalletProviders,
  );
  if (!newWallet) {
    throw new Error('newWallet is required in NetworkSelector');
  }

  const disabledNetworksForWallet = getDisabledNetworksForProvider(
    newWallet.walletOption.provider,
    disabledWalletProviders,
  );

  const filteredCurrencies = supportedCurrencies.filter(
    (currency) => !disabledNetworksForWallet.includes(currency.currencyNetwork),
  );

  let networks = buildNetworksFromSupportedCurrencies(intl, filteredCurrencies);

  // Filter networks based on provider support
  networks = networks.filter(
    (network) =>
      !isNetworkDisabledForProvider(
        newWallet.walletOption.provider,
        network.networkType,
        disabledWalletProviders,
      ),
  );

  const handleNetworkSelect = async (networkType: NetworkType) => {
    store.setValue((state) => {
      if (state.newWallet) {
        state.newWallet.networkType = networkType;
      }
    });

    const {provider} = newWallet.walletOption;
    const params =
      provider === 'WalletConnect'
        ? {
            provider,
            networkType,
            walletKey: newWallet.walletOption.key,
          }
        : {
            provider,
            networkType,
          };
    await startWalletConnection(params);
  };

  const isSingleNetwork = networks.length === 1;

  if (isSingleNetwork) {
    const {networkType, label, icons} = networks[0];

    return (
      <view.div
        css={{stack: 'y', gapY: 'space.150'}}
        data-testid="network-selector"
      >
        {/* Only one network type -- the network row is not-clickable */}
        <view.div css={networkRowStyles}>
          <Text type="bodyEmphasized">{label}</Text>

          <view.div css={{stack: 'x', gap: 'space.150', alignY: 'center'}}>
            <NetworkIconsStack icons={icons} />
          </view.div>
        </view.div>

        <GaslessPromotion
          networkType={networkType}
          walletProvider={newWallet.walletOption.provider}
        />

        <BrandedButton
          onClick={() => handleNetworkSelect(networkType)}
          loading={isWalletConnecting}
          textCss={{gapX: '8px', stack: 'x', alignY: 'center'}}
        >
          <Icon icon={external} size="xsmall" />

          <FormattedMessage {...messages.connectWallet} />
        </BrandedButton>
      </view.div>
    );
  }

  return (
    <view.div
      css={{stack: 'y', gapY: 'space.150'}}
      data-testid="network-selector"
    >
      {networks.map(({networkType, label, icons}) => (
        <Action
          key={networkType}
          onPress={() => handleNetworkSelect(networkType)}
          disabled={isWalletConnecting}
          css={{
            ...networkRowStyles,
            ':hover': {
              backgroundColor: COLORS.offsetHoverOnLight,
            },
          }}
        >
          <view.div css={{stack: 'y', width: 'fill', gapY: 'xsmall'}}>
            <view.div
              css={{
                stack: 'x',
                alignY: 'center',
                distribute: 'space-between',
                width: 'fill',
              }}
            >
              <Text type="bodyEmphasized">{label}</Text>

              <view.div css={{stack: 'x', gap: 'space.150', alignY: 'center'}}>
                <NetworkIconsStack icons={icons} />
                <Icon icon={chevronRight} size="xsmall" />
              </view.div>
            </view.div>
            <GaslessPromotion
              networkType={networkType}
              walletProvider={newWallet.walletOption.provider}
            />
          </view.div>
        </Action>
      ))}
    </view.div>
  );
};
